import { ActionReducerMapBuilder, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { EntityStoreName } from 'app/enums/store';
import { NotificationEntity } from 'app/interfaces/entity';
import { apiRequestCall } from 'app/services/api';
import { ApiResource } from 'app/services/api/enums';
import { cancelationSubscribe } from 'app/services/api/utils';
import { entityStoreStatus } from 'app/utils/store/entity-store-status';
import { entityAdapter } from 'modules/notifications/store/notifications/adapter';
import { NotificationsStoreStatus } from './enums';
import { store } from 'redux/store';
import { DEFAULT_PAGE_SIZE } from 'app/constants/api';
import {
  CreateTournamentAnnouncementRequest,
  NotificationsState,
} from 'modules/notifications/store/notifications/interfaces';
import { PaginatedResponse } from 'app/interfaces/api';
import { Identifier, NotificationStatus } from '@okletsplay/core';

export const getNotificationsList = createAsyncThunk(
  `${EntityStoreName.Notifications}/getNotificationsList`,
  (_, { signal }): Promise<PaginatedResponse<NotificationEntity[]>> => {
    const cancelToken = cancelationSubscribe(signal);
    const limit = store.getState()[EntityStoreName.Notifications].limit ?? DEFAULT_PAGE_SIZE;
    const offset = store.getState()[EntityStoreName.Notifications].offset ?? 0;
    const reload = store.getState()[EntityStoreName.Notifications].reload ?? true;

    return apiRequestCall<PaginatedResponse<NotificationEntity[]>>({
      params: {
        method: 'GET',
        resource: ApiResource.Notifications,
        params: {
          limit: reload ? Math.max(offset, limit) : limit,
          offset: reload ? 0 : offset,
        },
        cancelToken,
      },
    });
  }
);

export const getNotificationsCount = createAsyncThunk(
  `${EntityStoreName.Notifications}/getNotificationsCount`,
  (status: NotificationStatus, { signal }): Promise<{ count: number }> => {
    const cancelToken = cancelationSubscribe(signal);

    return apiRequestCall<{ count: number }>({
      params: {
        method: 'GET',
        resource: ApiResource.Notifications,
        urlPrefix: 'count',
        params: { status },
        cancelToken,
      },
    });
  }
);

export const updateNotification = createAsyncThunk(
  `${EntityStoreName.Notifications}/updateNotification`,
  (id: Identifier, { signal }): Promise<NotificationEntity> => {
    const cancelToken = cancelationSubscribe(signal);

    return apiRequestCall<NotificationEntity>({
      params: {
        method: 'PATCH',
        resource: ApiResource.Notifications,
        urlPrefix: id,
        data: { seen: true },
        cancelToken,
      },
    });
  }
);

export const createTournamentAnnouncement = createAsyncThunk(
  `${EntityStoreName.Notifications}/createAnnouncement`,
  (data: CreateTournamentAnnouncementRequest, { signal }): Promise<void> => {
    const cancelToken = cancelationSubscribe(signal);

    const { tournamentId, message } = data;
    return apiRequestCall({
      params: {
        method: 'POST',
        resource: ApiResource.Tournaments,
        urlPrefix: `${tournamentId}/announcements`,
        data: { message },
        cancelToken,
      },
      successRequestMessage: 'Announcement successfully created',
    });
  }
);

export const markAllAsRead = createAsyncThunk(
  `${EntityStoreName.Notifications}/markAllAsRead`,
  (): Promise<void> => {
    return apiRequestCall<void>({
      params: {
        method: 'PATCH',
        resource: ApiResource.Notifications,
        urlPrefix: 'mark-all-as-read',
      },
    });
  }
);

export const setOffsetReducer = (
  state: NotificationsState,
  action: PayloadAction<number>
): void => {
  state.offset = action.payload;
};

export const setReloadReducer = (
  state: NotificationsState,
  action: PayloadAction<boolean>
): void => {
  state.reload = action.payload;
};

export const setLimitReducer = (state: NotificationsState, action: PayloadAction<number>): void => {
  state.limit = action.payload;
};

export const registerReducers = (builder: ActionReducerMapBuilder<NotificationsState>): void => {
  builder
    .addCase(getNotificationsList.pending, (state, action) => {
      entityStoreStatus.addRequestId(
        state,
        NotificationsStoreStatus.Loading,
        action.meta.requestId
      );
    })
    .addCase(getNotificationsList.fulfilled, (state, action) => {
      if (state.reload || !state.offset) {
        entityAdapter.setAll(state, action.payload.data);
      } else {
        entityAdapter.addMany(state, action.payload.data);
      }

      state.total = action.payload.total;
      state.offset = state.reload
        ? action.payload.data.length
        : state.offset + action.payload.data.length;
      entityStoreStatus.removeRequestId(
        state,
        NotificationsStoreStatus.Loading,
        action.meta.requestId
      );
    })
    .addCase(getNotificationsList.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        NotificationsStoreStatus.Loading,
        action.meta.requestId
      );
    })
    .addCase(markAllAsRead.pending, (state, action) => {
      entityStoreStatus.addRequestId(
        state,
        NotificationsStoreStatus.MarkAllAsRead,
        action.meta.requestId
      );
    })
    .addCase(markAllAsRead.fulfilled, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        NotificationsStoreStatus.MarkAllAsRead,
        action.meta.requestId
      );
    })
    .addCase(markAllAsRead.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        NotificationsStoreStatus.MarkAllAsRead,
        action.meta.requestId
      );
    })
    .addCase(updateNotification.pending, (state, action) => {
      entityStoreStatus.addRequestId(
        state,
        NotificationsStoreStatus.Updating,
        action.meta.requestId
      );
    })
    .addCase(updateNotification.fulfilled, (state, action) => {
      entityAdapter.updateOne(state, { id: action.payload.id, changes: action.payload });
      entityStoreStatus.removeRequestId(
        state,
        NotificationsStoreStatus.Updating,
        action.meta.requestId
      );
    })
    .addCase(updateNotification.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        NotificationsStoreStatus.Updating,
        action.meta.requestId
      );
    })
    .addCase(getNotificationsCount.pending, (state, action) => {
      entityStoreStatus.addRequestId(
        state,
        NotificationsStoreStatus.NotificationsCountLoading,
        action.meta.requestId
      );
    })
    .addCase(getNotificationsCount.fulfilled, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        NotificationsStoreStatus.NotificationsCountLoading,
        action.meta.requestId
      );
    })
    .addCase(getNotificationsCount.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        NotificationsStoreStatus.NotificationsCountLoading,
        action.meta.requestId
      );
    })
    .addCase(createTournamentAnnouncement.pending, (state, action) => {
      const status = NotificationsStoreStatus.Creating;
      entityStoreStatus.addRequestId(state, status, action.meta.requestId);
    })
    .addCase(createTournamentAnnouncement.fulfilled, (state, action) => {
      const status = NotificationsStoreStatus.Creating;
      entityStoreStatus.removeRequestId(state, status, action.meta.requestId);
    })
    .addCase(createTournamentAnnouncement.rejected, (state, action) => {
      const status = NotificationsStoreStatus.Creating;
      entityStoreStatus.removeRequestId(state, status, action.meta.requestId);
    });
};
